import { useCallback } from 'react';
import { Filter, PauseCircle } from 'lucide-react';
import { t } from '@lingui/core/macro';
import { useSearchParams } from 'react-router-dom';
import { MultipleChoiceFilter } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilter';
import type { MultipleChoiceFilterOption } from '@duke-hq/ui/components/MultipleChoice/MultipleChoiceFilterTypes';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { StudentWorkPlacementJourneyStatus } from '@duke-hq/svc-api-db';
import { journeyStatusMetadata } from '@duke-hq/ui/components/PlacementStatusBadge/PlacementStatusBadge';
import { useSearchParamMultiFilter } from '@duke-hq/filters/hooks/useSearchParamMultiFilter';

export type PlacementStatusFilterValue = {
  statuses: StudentWorkPlacementJourneyStatus[];
  onHold: boolean;
};

const journeyStatusIds = Object.values(StudentWorkPlacementJourneyStatus);

export function usePlacementStatusFilter(): PlacementStatusFilterValue {
  const [searchParams] = useSearchParams();
  const { values: statuses } = useSearchParamMultiFilter<StudentWorkPlacementJourneyStatus>(
    'statuses',
    journeyStatusIds,
  );
  return { statuses, onHold: searchParams.get('onHold') === 'true' };
}

export function PlacementStatusFilter(): React.JSX.Element {
  const { values: statuses, setValues: setStatuses } =
    useSearchParamMultiFilter<StudentWorkPlacementJourneyStatus>('statuses', journeyStatusIds);
  const statusOptions = Object.entries(journeyStatusMetadata).map(([id, meta]) => ({
    id,
    value: id,
    label: meta.label,
  }));

  const handleChange = useCallback(
    (filters: MultipleChoiceFilterOption) => {
      setStatuses(filters.statuses ?? []);
    },
    [setStatuses],
  );

  return (
    <MultipleChoiceFilter
      items={statusOptions}
      label={t`Status`}
      name="statuses"
      onFilterChange={handleChange}
      onlyUpdateOnChange
      value={statuses.join(',')}
      variant="ghost"
      leftIcon={<Filter size={iconSize.md} />}
      rightIcon={undefined}
    />
  );
}

export function PlacementOnHoldFilter(): React.JSX.Element {
  const [, setSearchParams] = useSearchParams();
  const { onHold } = usePlacementStatusFilter();

  const handleChange = useCallback(
    (filters: MultipleChoiceFilterOption) => {
      const nextOnHold = (filters.onHold ?? []).includes('true');
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (nextOnHold) {
            next.set('onHold', 'true');
          } else {
            next.delete('onHold');
          }
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <MultipleChoiceFilter
      items={[{ id: 'true', value: 'true', label: t`On hold` }]}
      label={t`On hold`}
      name="onHold"
      onFilterChange={handleChange}
      onlyUpdateOnChange
      showSearch={false}
      value={onHold ? 'true' : ''}
      variant="ghost"
      leftIcon={<PauseCircle size={iconSize.md} />}
      rightIcon={undefined}
    />
  );
}
